import React, { useMemo, useEffect } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { compose } from 'redux';
import { lt } from 'lodash';

// CORE COMPONENTS
import Form from 'core/form/Form';
import Sprite from 'core/tools/Sprite';

// LOCAL VARIABLES
import { MODEL_SELECTION_OPTIONS, YEARS_SELECTION_OPTIONS } from '../options.js';

// GLOBAL VARIABLES
import { NEW_USED_TYPES } from 'globals.js';

// GLOBAL HELPERS
import { withRouter } from 'helpers/withRouter';
import useRecordView from 'helpers/getRecordView';

// GLOBAL PARTS
import Record from 'parts/record/Record';

// LOCAL COMPONENTS
import RebateModels from './RebateModels.js';
import CustomerRebatesAttachment from './CustomerRebatesAttachment.js';

// STYLES
import styles from './customerRebatesForm.module.scss';

// MAIN COMPONENT
const CustomerRebatesForm = compose(withRouter)(({ location: { search } }) => {

  // CONTEXT
  const { values, setFieldValue, setErrors, errors } = useFormikContext() || {};
  const { isEdit } = useRecordView();

  // SELECTOR
  const dealershipID = useSelector(({ dealership }) => dealership);

  // MEMOS
  const newUsedParam = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('new_used');
  }, [search]);

  const newUsed = useMemo(() => (newUsedParam ? +newUsedParam : values?.new_used), [newUsedParam, values]);

  const newUsedLabel = useMemo(() => NEW_USED_TYPES[newUsed], [newUsed]);

  const {
    auto_rebate,
    require_customer_signature,
    effective_date,
    expiration_date
  } = useMemo(() => values || {}, [values]);

  const hasAutoRebate = useMemo(() => !!parseInt(auto_rebate), [auto_rebate]);

  const dateError = useMemo(() => {
    const effectiveDate = new Date(effective_date).getTime();
    const expirationDate = new Date(expiration_date).getTime();

    return lt(expirationDate, effectiveDate);
  }, [effective_date, expiration_date]);

  useEffect(() => {
    const isSpecificTrim = values?.rebate_models?.find((model) => String(model?.trim_selection) === '1' || false) || false;
    if (isSpecificTrim) {
      setFieldValue('trim_selection', 1)
    } else {
      setFieldValue('trim_selection', 0)
    }
  }, [values.rebate_models, setFieldValue])

  // EFFECTS
  useEffect(() => {
    if (!isEdit) {
      setFieldValue('new_used', newUsed);
      setFieldValue('make_dealer_id', dealershipID);
    }
  }, [isEdit, setFieldValue, newUsed, dealershipID]);

  useEffect(() => {
    if (!dateError) {
      setErrors({
        ...errors,
        dummy: {
          effective_date: null,
          expiration_date: null
        }
      });
    }
  }, [setErrors, dateError, errors]);

  // RENDER
  return (
    <Form.Body>
      <Form.PlainText label="Type" value={newUsedLabel} />
      <Form.YesNo name="is_active" label="Active" required />
      <Form.Control name="rebate_title" label="Rebate Program Name (Title)" required />
      <Form.Control name="rebate_program_code" label="Rebate Program Code" />
      <Form.Control name="rebate_amount" label="Rebate Amount" schema="dollars" required />
      <Form.YesNo name="rebate_amount_lock" label="Lock Rebate Amount" required />
      <Form.Date
        name="effective_date"
        label="Effective Date"
        maxDate={new Date(expiration_date)}
        error={
          dateError ? (
            <div className="d-flex">
              <Sprite className="me-2" use="warning" fill="danger" />A valid Effective Date that is less than the
              Expiration Date is required.
            </div>
          ) : undefined
        }
      />
      <Form.Date
        name="expiration_date"
        label="Expiration Date"
        minDate={new Date(effective_date)}
        error={
          dateError ? (
            <div className="d-flex">
              <Sprite className="me-2" use="warning" fill="danger" />A valid Expiration Date that is greater than the
              Effective Date is required.
            </div>
          ) : undefined
        }
      />
      <Form.Textarea name="rebate_description" label="Description" />
      <Form.YesNo name="auto_rebate" label="Auto Apply Rebate" required />
      {hasAutoRebate && (
        <Form.Checklist
          className={styles.yearSelection}
          type="radio"
          name="years_selection"
          label="Years"
          options={YEARS_SELECTION_OPTIONS}
          list={{
            direction: 'vertical'
          }}
          required
          useCSV
        />
      )}
      {hasAutoRebate && (
        <Form.Checklist
          type="radio"
          name="model_selection"
          label="Model Selection"
          options={MODEL_SELECTION_OPTIONS}
          useCSV
          required
        />
      )}
      <RebateModels />
      <CustomerRebatesAttachment
        name="documentAttachment.rebate_program_docs"
        label="Factory Program"
        documentType="factoryProgram"
      />
      <Form.YesNo name="require_customer_signature" label="Does the Program Require Customer Signature?" required />
      {parseInt(require_customer_signature) === 1 && (
        <CustomerRebatesAttachment
          name="documentAttachment.customer_sig_forms"
          label="Customer Signature Form"
          documentType="customerSig"
          required
        />
      )}
      <Record.Log />
    </Form.Body>
  );
});
// EXPORT
export default CustomerRebatesForm;
